import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchDailyReport,
  fetchNonWorkingDays,
  fetchPortfolio,
  fetchPresenterOrder,
  fetchRefUsers,
  fetchReportAccess,
  fetchReportGrid,
  type GridFilter,
} from './api';

export function useReportAccess() {
  return useQuery({
    queryKey: ['worklog-report', 'access'],
    queryFn: fetchReportAccess,
    staleTime: 60_000,
  });
}

export function useReportUsers(enabled = true) {
  return useQuery({
    queryKey: ['ref', 'users'],
    queryFn: fetchRefUsers,
    staleTime: 5 * 60_000,
    enabled,
  });
}

const GRID_STALE = 30_000;

/** 그리드 쿼리 키 — 조회(useReportGrid)와 선반입(usePrefetchReportGrid)이 반드시 공유해야 한다.
 *  갈라지면 선반입이 다른 칸을 채워 발표 중 전환이 그대로 스피너를 띄운다.
 *  filter.presenter는 의도적으로 키에 넣지 않는다 — 응답 데이터는 발표 모드 여부와 무관하게
 *  동일하고(서버는 감사 스킵 여부만 다르게 처리), 키에 넣으면 발표 모드 진입/이탈 시 같은
 *  데이터를 이중 캐시하게 된다(api.ts GridFilter.presenter 주석 참고). */
export function gridQueryKey(filter: GridFilter) {
  return [
    'worklog-report',
    'grid',
    filter.start,
    filter.weeks,
    filter.userIds.join(','),
    filter.deptId ?? null,
    filter.rank ?? null,
    filter.detail ?? false,
    filter.body ?? false,
    filter.today,
  ];
}

export function useReportGrid(filter: GridFilter, enabled = true) {
  return useQuery({
    queryKey: gridQueryKey(filter),
    queryFn: () => fetchReportGrid(filter),
    staleTime: GRID_STALE,
    enabled,
  });
}

/** 발표 모드 전용 — 다음/이전 발표자의 그리드를 미리 받아둬 전환 시 스피너를 없앤다. */
export function usePrefetchReportGrid() {
  const qc = useQueryClient();
  return useCallback(
    (filter: GridFilter) => {
      qc.prefetchQuery({
        queryKey: gridQueryKey(filter),
        queryFn: () => fetchReportGrid(filter),
        staleTime: GRID_STALE,
      });
    },
    [qc],
  );
}

/** 발표자 담당 프로젝트 패널 — 집계형·저빈도라 5분 캐시. 접힘 상태에서도 헤더 카운트용으로 조회한다. */
const PORTFOLIO_STALE = 5 * 60_000;

export function portfolioQueryKey(userId: number, today: string) {
  return ['worklog-report', 'portfolio', userId, today];
}

export function usePortfolio(userId: number | null, today: string) {
  return useQuery({
    queryKey: portfolioQueryKey(userId ?? 0, today),
    queryFn: () => fetchPortfolio(userId as number, today),
    staleTime: PORTFOLIO_STALE,
    enabled: userId != null,
  });
}

/** 발표 모드 전용 — 다음/이전 발표자의 패널 데이터 선반입 (그리드 prefetch와 동일 이유) */
export function usePrefetchPortfolio() {
  const qc = useQueryClient();
  return useCallback(
    (userId: number, today: string) => {
      qc.prefetchQuery({
        queryKey: portfolioQueryKey(userId, today),
        queryFn: () => fetchPortfolio(userId, today),
        staleTime: PORTFOLIO_STALE,
      });
    },
    [qc],
  );
}

/** presenter=true면 발표 모드 중 열람임을 서버에 알려 교차 열람 감사를 스킵시킨다.
 *  queryKey에는 넣지 않는다 — gridQueryKey와 동일한 이유(api.ts GridFilter.presenter 주석). */
export function useDailyReport(userId: number | null, date: string | null, presenter = false) {
  return useQuery({
    queryKey: ['worklog-report', 'daily', userId, date],
    queryFn: () => fetchDailyReport(userId as number, date as string, presenter),
    staleTime: 30_000,
    enabled: userId != null && date != null,
  });
}

/** 발표 순회 순서 — 공유 설정. 다른 사람이 자리 순서를 고쳤을 수 있으므로 짧게(30초) 잡는다. */
export function usePresenterOrder(enabled = true) {
  return useQuery({
    queryKey: ['worklog-report', 'presenter-order'],
    queryFn: fetchPresenterOrder,
    staleTime: 30_000,
    enabled,
  });
}

export function useNonWorkingDays(enabled = true) {
  return useQuery({
    queryKey: ['worklog-report', 'non-working-days'],
    queryFn: fetchNonWorkingDays,
    staleTime: 5 * 60_000,
    enabled,
  });
}
